import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Sun,
  Trash2,
  X,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { InstallButtons } from '@/components/install/InstallButtons';
import { buildMcpConfigJson } from '@/lib/install';
import { SettingsPlanGrid } from './SettingsPlanGrid';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { useTheme } from '@/hooks/useTheme';
import type { Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import {
  STATUS_LABEL,
  createDomain,
  deleteDomain,
  listDomains,
  verifyDomain,
  type DomainDnsRecord,
  type DomainStatus,
  type SendingDomain,
} from '@/lib/domains';
import { ApiError } from '@/lib/api';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKeySummary,
  type CreatedApiKey,
} from '@/lib/api-keys';
import { openBillingPortal } from '@/lib/billing';
import { getUsage, type QuotaStatus, type UsageSnapshot } from '@/lib/usage';
import { PRICING_DISPLAY } from '@/lib/waitlist';
import { formatRelativeTime } from '@/lib/drafts';
import {
  deleteAccount,
  getAccount,
  removeAvatar,
  uploadAvatar,
  type AccountResponse,
  type SignInProvider,
} from '@/lib/account';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  getKlaviyoStatus,
  connectKlaviyo,
  testKlaviyoConnection,
  disconnectKlaviyo,
  type KlaviyoStatus,
} from '@/lib/integrations';

type SettingsTab =
  | 'domains'
  | 'account'
  | 'api'
  | 'install'
  | 'usage'
  | 'plan'
  | 'integrations'
  | 'appearance';

const TABS: Array<{ id: SettingsTab; label: string; ready: boolean }> = [
  { id: 'domains', label: 'Sending domains', ready: true },
  { id: 'plan', label: 'Plan', ready: true },
  { id: 'usage', label: 'Usage', ready: true },
  { id: 'api', label: 'API keys', ready: true },
  { id: 'install', label: 'Install in AI tool', ready: true },
  { id: 'integrations', label: 'Integrations', ready: true },
  { id: 'appearance', label: 'Appearance', ready: true },
  { id: 'account', label: 'Account', ready: true },
];

export function SettingsPage() {
  const { user, refresh: refreshSession } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as SettingsTab) || 'domains';

  function setTab(t: SettingsTab) {
    setParams({ tab: t }, { replace: true });
  }

  // When the user lands back here from Stripe Checkout (`?billing=success`),
  // the webhook may have already flipped the tier — re-fetch the session so
  // the UI flips too. Lives here (not inside Plan section) so it fires
  // regardless of which tab Stripe sent us to: this means old cached
  // success URLs that still say `?tab=usage&billing=success` keep working.
  useEffect(() => {
    if (params.get('billing') === 'success') void refreshSession();
    // We only want to react to the param landing, not to refresh re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('billing')]);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <AppHeader currentTab="settings" />

      {/* Two-column layout. Both the sidebar AND the content live inside
          the same `mx-auto max-w-[1200px] px-6 lg:px-10` container as
          <AppHeader />, so the sidebar's left edge lines up vertically
          with the SendFast wordmark above it.
          Mobile (< lg): sidebar collapses to a horizontal scroll-strip
          above the content. */}
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <div className="lg:flex lg:items-start lg:gap-10">
          <aside
            className={cn(
              'border-border border-b',
              // Sticky pane on the left, anchored just below the 56px
              // AppHeader. Solid bg + border-r divider keep cards from
              // ever bleeding through visually.
              'lg:sticky lg:top-14 lg:h-[calc(100vh-3.5rem)] lg:w-44 lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-r',
            )}
          >
            <nav className="py-4 lg:py-8 lg:pr-4">
              <ul className="flex gap-1 overflow-x-auto text-sm lg:flex-col lg:overflow-x-visible">
                {TABS.map((t) => (
                  <li key={t.id} className="shrink-0 lg:shrink">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => t.ready && setTab(t.id)}
                      disabled={!t.ready}
                      className={cn(
                        'h-auto w-full justify-start whitespace-nowrap px-3 py-2 font-normal',
                        tab === t.id
                          ? 'bg-muted text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                        !t.ready && 'cursor-not-allowed opacity-40',
                      )}
                    >
                      {t.label}
                      {t.ready ? null : <span className="ml-2 text-xs">(soon)</span>}
                    </Button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="min-w-0 flex-1 py-10 lg:py-12">
            <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Manage your sending domains, account preferences, and API access.
            </p>

            <section className="mt-8">
              {tab === 'domains' ? (
                <DomainsSection />
              ) : tab === 'plan' ? (
                <PlanSection />
              ) : tab === 'usage' ? (
                <UsageSection userEmail={user?.email} />
              ) : tab === 'api' ? (
                <ApiKeysSection />
              ) : tab === 'install' ? (
                <InstallSection />
              ) : tab === 'integrations' ? (
                <IntegrationsSection />
              ) : tab === 'appearance' ? (
                <AppearanceSection />
              ) : tab === 'account' ? (
                <AccountSection />
              ) : (
                <ComingSoon />
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Domains section
// ===========================================================================

function DomainsSection() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<SendingDomain[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setDomains(await listDomains());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load domains');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-poll every 30s if any domain is still pending — this catches DNS
  // propagation without the user having to click "Check status" themselves.
  useEffect(() => {
    if (!domains) return;
    const hasPending = domains.some((d) => d.status === 'pending');
    if (!hasPending) return;
    const t = setInterval(() => {
      void refresh();
    }, 30_000);
    return () => clearInterval(t);
  }, [domains, refresh]);

  async function handleVerify(id: string) {
    setVerifyingId(id);
    setError(null);
    try {
      const updated = await verifyDomain(id);
      setDomains((prev) => prev?.map((d) => (d.id === id ? updated : d)) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setVerifyingId(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this domain? You will need to re-verify if you add it back.')) return;
    setError(null);
    try {
      await deleteDomain(id);
      setDomains((prev) => prev?.filter((d) => d.id !== id) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  function handleCreated(domain: SendingDomain) {
    setDomains((prev) => (prev ? [domain, ...prev] : [domain]));
    setShowModal(false);
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Sending domains</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Connect your own domain so emails are sent from your address (e.g.{' '}
            <code className="text-foreground">hello@yourdomain.com</code>) and look like they come from you.
          </p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="size-4" /> Add domain
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive" className="mt-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {domains === null ? (
        <DomainsLoading />
      ) : domains.length === 0 ? (
        <DomainsEmpty onAdd={() => setShowModal(true)} />
      ) : (
        <ul className="mt-6 space-y-4">
          {domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              verifying={verifyingId === d.id}
              onVerify={() => handleVerify(d.id)}
              onDelete={() => handleDelete(d.id)}
              onUseInDraft={() => navigate('/app')}
            />
          ))}
        </ul>
      )}

      {showModal ? (
        <AddDomainModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </div>
  );
}

function DomainsLoading() {
  return (
    <ul className="mt-6 space-y-4">
      {[0, 1].map((i) => (
        <li key={i}>
          <Card className="p-5">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-3 h-3 w-2/3" />
            <Skeleton className="mt-2 h-3 w-1/2" />
          </Card>
        </li>
      ))}
    </ul>
  );
}

function DomainsEmpty({ onAdd }: { onAdd: () => void }) {
  return (
    <Card className="border-border mt-8 border-dashed text-center">
      <div className="p-10">
        <h3 className="text-lg font-semibold tracking-tight">No domains yet</h3>
        <p className="text-muted-foreground mx-auto mt-2 max-w-md text-sm">
          Until you add a domain, sends will use a SendFast shared address with low daily limits.
          Add your domain to send unlimited.
        </p>
        <Button className="mt-6" onClick={onAdd}>
          <Plus className="size-4" /> Add your first domain
        </Button>
      </div>
    </Card>
  );
}

function DomainCard({
  domain,
  verifying,
  onVerify,
  onDelete,
  onUseInDraft,
}: {
  domain: SendingDomain;
  verifying: boolean;
  onVerify: () => void;
  onDelete: () => void;
  onUseInDraft: () => void;
}) {
  const [open, setOpen] = useState(domain.status !== 'verified');

  return (
    <li>
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-start justify-between gap-4 p-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="truncate text-lg font-medium">{domain.domain}</h3>
              <StatusBadge status={domain.status} />
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              Added {new Date(domain.createdAt).toLocaleDateString()}
              {domain.lastCheckedAt ? (
                <> · last checked {new Date(domain.lastCheckedAt).toLocaleTimeString()}</>
              ) : null}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {domain.status === 'verified' ? (
              <Button variant="outline" size="sm" onClick={onUseInDraft}>
                Use in draft
              </Button>
            ) : (
              <Button size="sm" onClick={onVerify} disabled={verifying}>
                {verifying ? 'Checking…' : 'Check status'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
            >
              {open ? 'Hide DNS' : 'Show DNS'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Delete domain"
            >
              Remove
            </Button>
          </div>
        </div>

        {open ? <DnsRecordsTable records={domain.dnsRecords} status={domain.status} /> : null}
      </Card>
    </li>
  );
}

function StatusBadge({ status }: { status: DomainStatus }) {
  if (status === 'verified') {
    return (
      <Badge
        variant="outline"
        className="border-success/30 bg-success/10 text-success rounded-full text-xs font-medium"
      >
        <CheckCircle2 className="size-3" /> {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive" className="rounded-full text-xs font-medium">
        <AlertCircle className="size-3" /> {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === 'temporary_failure') {
    return (
      <Badge
        variant="outline"
        className="border-warn/30 bg-warn/10 text-warn rounded-full text-xs font-medium"
      >
        <AlertCircle className="size-3" /> {STATUS_LABEL[status]}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="rounded-full text-xs font-medium">
      <Clock className="size-3" /> {STATUS_LABEL[status]}
    </Badge>
  );
}

function DnsRecordsTable({
  records,
  status,
}: {
  records: DomainDnsRecord[];
  status: DomainStatus;
}) {
  return (
    <div className="border-border bg-muted/50 border-t">
      <div className="p-5">
        <p className="text-muted-foreground mb-4 text-sm">
          {status === 'verified'
            ? 'These records are live in your DNS. Keep them in place to keep sending from this domain.'
            : "Add the records below to your DNS provider (e.g. Cloudflare, Namecheap, Route 53). DNS changes can take a few minutes — we'll auto-check every 30 seconds."}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-left">
                <th className="pb-2 pr-3 font-medium">Purpose</th>
                <th className="pb-2 pr-3 font-medium">Type</th>
                <th className="pb-2 pr-3 font-medium">Name / Host</th>
                <th className="pb-2 pr-3 font-medium">Value</th>
                <th className="pb-2 font-medium">Priority</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <DnsRow key={`${r.type}-${r.name}-${i}`} record={r} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DnsRow({ record }: { record: DomainDnsRecord }) {
  return (
    <tr className="border-border border-b align-top last:border-0">
      <td className="text-muted-foreground whitespace-nowrap py-3 pr-3">{record.label}</td>
      <td className="py-3 pr-3 font-mono">{record.type}</td>
      <td className="py-3 pr-3">
        <CopyableValue value={record.name} />
      </td>
      <td className="max-w-[400px] py-3 pr-3">
        <CopyableValue value={record.value} multiline />
      </td>
      <td className="py-3 font-mono">{record.priority ?? '—'}</td>
    </tr>
  );
}

function CopyableValue({ value, multiline }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      type="button"
      onClick={copy}
      title="Click to copy"
      className={cn(
        'group text-foreground hover:text-primary inline-flex items-start gap-2 text-left font-mono',
        multiline ? 'break-all' : 'whitespace-nowrap',
      )}
    >
      <span>{value}</span>
      <span className="text-muted-foreground text-[10px] uppercase tracking-wider opacity-0 group-hover:opacity-100">
        {copied ? '✓ copied' : 'copy'}
      </span>
    </button>
  );
}

// ===========================================================================
// Add Domain Modal
// ===========================================================================

function AddDomainModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (d: SendingDomain) => void;
}) {
  const [domain, setDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = useMemo(() => {
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) return { ok: false, hint: '' };
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed))
      return { ok: false, hint: "That doesn't look like a domain" };
    if (trimmed.includes(' ')) return { ok: false, hint: 'No spaces allowed' };
    return { ok: true, hint: '' };
  }, [domain]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !validation.ok) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createDomain(domain.trim().toLowerCase());
      onCreated(created);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError(e instanceof Error ? e.message : 'Failed to add domain');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add a sending domain</DialogTitle>
            <DialogDescription>
              Use a domain you own (or a subdomain like{' '}
              <code className="text-foreground">mail.yourcompany.com</code>). After adding, you'll get
              DNS records to paste into your DNS provider.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-2">
            <Label>Domain</Label>
            <Input
              type="text"
              autoFocus
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourcompany.com"
              className="font-mono text-sm"
              disabled={submitting}
            />
            {validation.hint ? (
              <p className="text-muted-foreground text-xs">{validation.hint}</p>
            ) : null}
          </div>

          {error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!validation.ok || submitting}>
              {submitting ? 'Adding…' : 'Add domain'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Integrations section
// ===========================================================================

function IntegrationsSection() {
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Integrations</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Connect third-party services to export email templates directly from the editor.
          </p>
        </div>
      </div>
      <div className="mt-6">
        <KlaviyoCard />
      </div>
    </div>
  );
}

function KlaviyoCard() {
  const [status, setStatus] = useState<KlaviyoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [label, setLabel] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await getKlaviyoStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    if (connecting || !apiKey.trim()) return;
    setConnecting(true);
    setError(null);
    setSuccess(null);
    try {
      await connectKlaviyo(apiKey.trim(), label.trim() || undefined);
      setApiKey('');
      setLabel('');
      setSuccess('Klaviyo connected successfully!');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Connection failed');
    } finally {
      setConnecting(false);
      setTimeout(() => setSuccess(null), 4000);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await testKlaviyoConnection();
      setTestResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
      setTimeout(() => setTestResult(null), 5000);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect Klaviyo? Any draft-to-template mappings will also be removed.')) return;
    setError(null);
    try {
      await disconnectKlaviyo();
      setStatus({ connected: false });
      setSuccess('Klaviyo disconnected.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed');
    } finally {
      setTimeout(() => setSuccess(null), 4000);
    }
  }

  if (loading) {
    return (
      <Card className="p-5">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="mt-3 h-3 w-2/3" />
        <Skeleton className="mt-2 h-3 w-1/2" />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0 gap-0">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="flex min-w-0 items-center gap-3">
          {/* Klaviyo logo placeholder */}
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#16191f]">
            <span className="text-xs font-bold text-white">K</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Klaviyo</h3>
            <p className="text-muted-foreground mt-0.5 text-xs">Email marketing & automation platform</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status?.connected ? (
            <Badge
              variant="outline"
              className="border-success/30 bg-success/10 text-success rounded-full text-xs font-medium"
            >
              <span className="bg-success size-1.5 rounded-full" />
              Connected
            </Badge>
          ) : (
            <Badge variant="secondary" className="rounded-full text-xs font-medium">
              <span className="bg-muted-foreground size-1.5 rounded-full" />
              Not connected
            </Badge>
          )}
        </div>
      </div>

      {/* Error / Success */}
      {error ? (
        <Alert variant="destructive" className="mx-5 mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {success ? (
        <Alert className="border-success/30 bg-success/5 text-success mx-5 mb-4 [&>svg]:text-success *:data-[slot=alert-description]:text-success/90">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      ) : null}

      {status?.connected ? (
        /* ── Connected state ── */
        <div className="border-border bg-muted/50 border-t p-5">
          <dl className="space-y-2 text-sm">
            <div className="flex items-baseline gap-3">
              <dt className="text-muted-foreground w-20 shrink-0">API Key</dt>
              <dd className="font-mono text-xs">{status.key_prefix}</dd>
            </div>
            {status.label ? (
              <div className="flex items-baseline gap-3">
                <dt className="text-muted-foreground w-20 shrink-0">Label</dt>
                <dd>{status.label}</dd>
              </div>
            ) : null}
            {status.created_at ? (
              <div className="flex items-baseline gap-3">
                <dt className="text-muted-foreground w-20 shrink-0">Connected</dt>
                <dd>{new Date(status.created_at).toLocaleDateString()}</dd>
              </div>
            ) : null}
          </dl>

          <div className="mt-4 flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
              {testing ? 'Testing…' : 'Test connection'}
            </Button>
            {testResult ? (
              <span className={cn('text-xs', testResult.ok ? 'text-success' : 'text-destructive')}>
                {testResult.ok ? '✓ ' : '✗ '}
                {testResult.message}
              </span>
            ) : null}
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDisconnect}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        /* ── Disconnected / connect form ── */
        <div className="border-border bg-muted/50 border-t p-5">
          <p className="text-muted-foreground mb-4 text-sm">
            Connect your Klaviyo account to push email templates directly from the editor.
          </p>
          <form onSubmit={handleConnect} className="space-y-3">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">Private API Key</Label>
              <Input
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="pk_xxxxxxxxxxxx"
                disabled={connecting}
                className="bg-card font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">Label (optional)</Label>
              <Input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Production account"
                disabled={connecting}
                className="bg-card text-sm"
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Find your private API key at{' '}
              <a
                href="https://www.klaviyo.com/settings/account/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Klaviyo → Settings → API Keys ↗
              </a>
              . The key needs the <code className="text-foreground">templates:write</code> scope.
            </p>
            <div className="flex justify-end pt-1">
              <Button type="submit" disabled={!apiKey.trim() || connecting}>
                {connecting ? 'Connecting…' : 'Connect Klaviyo →'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </Card>
  );
}

// ===========================================================================
// Coming-soon placeholder
// ===========================================================================

// ===========================================================================
// Plan section
// ===========================================================================
//
// Owns everything subscription-related: the current-tier summary, renewal
// status, "Manage billing" CTA (Stripe portal), and the full pricing grid
// for upgrades / downgrades. Lives separate from Usage so users have a
// clean place to think about money without scrolling past quota bars.

function PlanSection() {
  const { user } = useSession();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const tier = user?.tier ?? 'free';
  const tierMeta = PRICING_DISPLAY[tier as 'free' | 'pro' | 'scale'];

  async function handleManageBilling() {
    setPortalError(null);
    setPortalLoading(true);
    try {
      await openBillingPortal();
    } catch (err) {
      setPortalLoading(false);
      setPortalError(err instanceof ApiError ? err.message : 'Could not open billing portal');
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Plan</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            You're on the <span className="text-foreground font-medium">{tierMeta.name}</span>{' '}
            plan.{' '}
            {tier === 'free'
              ? 'Upgrade for higher limits and faster generations.'
              : 'Change card, switch tiers, or cancel from the billing portal.'}
          </p>
          {tier !== 'free' ? <SubscriptionStatusLine user={user} /> : null}
        </div>

        {tier !== 'free' && user?.hasBillingCustomer ? (
          <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading}>
            {portalLoading ? 'Opening…' : 'Manage billing'}
          </Button>
        ) : null}
      </div>

      {portalError ? (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{portalError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="mt-8">
        <SettingsPlanGrid
          currentTier={tier}
          hasBillingCustomer={user?.hasBillingCustomer ?? false}
        />
      </div>
    </div>
  );
}

// ===========================================================================
// Usage section
// ===========================================================================

function UsageSection({ userEmail: _userEmail }: { userEmail?: string }) {
  const navigate = useNavigate();
  const { user } = useSession();
  const [usage, setUsage] = useState<UsageSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tier = user?.tier ?? 'free';
  const tierMeta = PRICING_DISPLAY[tier as 'free' | 'pro' | 'scale'];

  useEffect(() => {
    (async () => {
      try {
        setUsage(await getUsage());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load usage');
      }
    })();
  }, []);

  const anyExceeded = useMemo(() => {
    if (!usage) return false;
    return (
      usage.ai_generates.exceeded ||
      usage.ai_modifies.exceeded ||
      usage.sends.exceeded
    );
  }, [usage]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Usage</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Your monthly counters against the{' '}
            <span className="text-foreground font-medium">{tierMeta.name}</span> plan limits.
            {usage ? <> Resets on the 1st of each month (period {usage.period}).</> : null}
          </p>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive" className="mt-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {usage === null ? (
        <UsageLoading />
      ) : (
        <>
          <div className="mt-6 space-y-4">
            <QuotaBar label="AI generations" status={usage.ai_generates} unit="generations" />
            <QuotaBar label="AI edits" status={usage.ai_modifies} unit="edits" />
            <QuotaBar label="Email sends" status={usage.sends} unit="sends" />
          </div>

          {anyExceeded ? (
            <Card className="border-destructive/30 bg-destructive/5 mt-6 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="max-w-xl">
                  <h3 className="text-destructive text-sm font-medium">
                    You've hit one of your {tierMeta.name} limits
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {tier === 'free'
                      ? 'Upgrade to Pro for 25× more sends and edits, plus more recipients per send, domains, and API keys.'
                      : tier === 'pro'
                        ? 'Upgrade to Scale to unlock 10× the headroom — or drop us a line for a custom plan.'
                        : 'Drop us a line to scope a custom plan with higher limits.'}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/app/settings?tab=plan')}
                >
                  View plans →
                </Button>
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

/** "Renews on …" / "Cancels on …" / past-due banner. */
function SubscriptionStatusLine({ user }: { user: import('@/hooks/useSession').SessionUser | null }) {
  if (!user || user.tier === 'free') return null;
  const status = user.subscriptionStatus;
  const periodEnd = user.currentPeriodEnd
    ? new Date(user.currentPeriodEnd * 1000).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  if (status === 'past_due' || status === 'unpaid') {
    return (
      <p className="text-destructive mt-1 text-sm">
        Last payment failed — update your card via Manage billing to keep your plan active.
      </p>
    );
  }
  if (user.cancelAtPeriodEnd && periodEnd) {
    return (
      <p className="text-muted-foreground mt-1 text-sm">
        Cancels on <span className="text-foreground">{periodEnd}</span> — you'll drop to Free after.
      </p>
    );
  }
  if (periodEnd) {
    return (
      <p className="text-muted-foreground mt-1 text-sm">
        Renews on <span className="text-foreground">{periodEnd}</span>.
      </p>
    );
  }
  return null;
}

function UsageLoading() {
  return (
    <div className="mt-6 space-y-4">
      {[0, 1, 2].map((i) => (
        <Card key={i} className="p-4">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="mt-3 h-2.5 w-full rounded-full" />
        </Card>
      ))}
    </div>
  );
}

function QuotaBar({
  label,
  status,
  unit,
}: {
  label: string;
  status: QuotaStatus;
  unit: string;
}) {
  const cap = status.limit + status.bonus;
  const pct = cap === 0 ? 0 : Math.min(100, Math.round((status.used / cap) * 100));
  const danger = status.exceeded;
  const warn = !danger && pct >= 80;
  const barColor = danger ? 'bg-destructive' : warn ? 'bg-warn' : 'bg-primary';

  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{label}</span>
        <span className={cn('text-xs', danger ? 'text-destructive font-medium' : 'text-muted-foreground')}>
          {status.used.toLocaleString()} / {cap.toLocaleString()} {unit}
          {status.bonus > 0 ? (
            <span className="text-muted-foreground ml-1">
              (+{status.bonus.toLocaleString()} bonus)
            </span>
          ) : null}
        </span>
      </div>
      <div className="bg-muted mt-3 h-2 w-full overflow-hidden rounded-full">
        <div className={cn('h-full transition-all', barColor)} style={{ width: `${pct}%` }} />
      </div>
      {danger ? (
        <p className="text-destructive mt-2 text-xs">
          Limit reached — further {label.toLowerCase()} requests are blocked until next period.
        </p>
      ) : null}
    </Card>
  );
}

// ===========================================================================
// API keys section
// ===========================================================================

function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [limit, setLimit] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);

  const activeCount = useMemo(() => {
    if (!keys) return 0;
    return keys.filter((k) => !k.revoked_at).length;
  }, [keys]);
  const atLimit = activeCount >= limit;

  const refresh = useCallback(async () => {
    try {
      const data = await listApiKeys();
      setKeys(data.keys);
      setLimit(data.limit);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API keys');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (creating || atLimit) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createApiKey(newKeyName.trim() || undefined);
      setRevealedKey(created);
      setNewKeyName('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this key? Any apps using it will stop working immediately.')) return;
    setError(null);
    try {
      await revokeApiKey(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke key');
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">API keys</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Use a key to authenticate REST and MCP calls. Pass it as{' '}
            <code className="text-foreground">Authorization: Bearer sf_live_…</code>.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href="/docs" target="_blank" rel="noreferrer">
            Read the docs ↗
          </a>
        </Button>
      </div>

      {revealedKey ? (
        <RevealedKeyCard created={revealedKey} onDismiss={() => setRevealedKey(null)} />
      ) : (
        <Card className="mt-6 p-4">
          <form onSubmit={handleCreate}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1 space-y-2">
                <Label className="text-muted-foreground text-xs">Key name (optional)</Label>
                <Input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g. Production server"
                  disabled={creating || atLimit}
                  maxLength={80}
                />
              </div>
              <Button type="submit" disabled={creating || atLimit}>
                {creating ? (
                  'Creating…'
                ) : (
                  <>
                    <Plus className="size-4" /> Create key
                  </>
                )}
              </Button>
            </div>
            {atLimit ? (
              <p className="text-muted-foreground mt-3 text-xs">
                You have {activeCount} active key{activeCount === 1 ? '' : 's'} of {limit} allowed on
                Free. Revoke one to create another, or upgrade for more.
              </p>
            ) : null}
          </form>
        </Card>
      )}

      {error ? (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {keys === null ? (
        <KeysLoading />
      ) : keys.length === 0 ? (
        <Card className="border-border mt-6 border-dashed text-center">
          <div className="p-8">
            <p className="text-muted-foreground text-sm">No API keys yet.</p>
          </div>
        </Card>
      ) : (
        <ul className="mt-6 space-y-2">
          {keys.map((k) => (
            <KeyRow key={k.id} apiKey={k} onRevoke={() => handleRevoke(k.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KeysLoading() {
  return (
    <ul className="mt-6 space-y-2">
      {[0, 1].map((i) => (
        <li key={i}>
          <Card className="p-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="mt-2 h-3 w-1/4" />
          </Card>
        </li>
      ))}
    </ul>
  );
}

function RevealedKeyCard({
  created,
  onDismiss,
}: {
  created: CreatedApiKey;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(created.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <Card className="border-primary/30 bg-primary/[0.04] mt-6 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Your new API key is ready</h3>
          <p className="text-muted-foreground mt-1 text-xs">
            Copy it now — we won't show it again. Treat it like a password.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="bg-background border-border mt-3 flex items-center gap-2 rounded-md border p-3">
        <code className="min-w-0 flex-1 break-all font-mono text-xs">{created.key}</code>
        <Button variant="outline" size="sm" onClick={copy}>
          {copied ? (
            <>
              <Check className="size-3" /> Copied
            </>
          ) : (
            'Copy'
          )}
        </Button>
      </div>
    </Card>
  );
}

function KeyRow({
  apiKey,
  onRevoke,
}: {
  apiKey: ApiKeySummary;
  onRevoke: () => void;
}) {
  const revoked = !!apiKey.revoked_at;
  return (
    <li>
      <Card className={cn('flex flex-row flex-wrap items-center justify-between gap-4 p-4', revoked && 'opacity-60')}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="truncate text-sm font-medium">{apiKey.name || 'Unnamed key'}</span>
            {revoked ? (
              <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">
                Revoked
              </Badge>
            ) : null}
          </div>
          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-xs">
            <code className="font-mono">{apiKey.key_prefix}…</code>
            <span>·</span>
            <span>Created {new Date(apiKey.created_at).toLocaleDateString()}</span>
            {apiKey.last_used_at ? (
              <>
                <span>·</span>
                <span>Last used {formatRelativeTime(apiKey.last_used_at)}</span>
              </>
            ) : null}
          </div>
        </div>
        {!revoked ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRevoke}
            className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            Revoke
          </Button>
        ) : null}
      </Card>
    </li>
  );
}

function ComingSoon() {
  return (
    <Card className="border-border mt-2 border-dashed text-center">
      <div className="p-10">
        <p className="text-muted-foreground text-sm">
          This section is coming soon. For now, manage your account through the sign-out flow and
          your domains in the tab on the left.
        </p>
      </div>
    </Card>
  );
}

// ===========================================================================
// Install section — one-click MCP setup
// ===========================================================================
//
// Mirrors the public /install page but trimmed for in-app use: the user is
// already signed in, so we go straight to the buttons + a manual snippet
// for hosts that don't have a deeplink. The full per-client guide
// (Claude Desktop config paths, Claude.ai connector flow, etc.) lives at
// /install — we link out for it rather than duplicating the layout here.

const MCP_TOOLS = [
  { name: 'draft_email', desc: 'Generate a brand-new HTML email from a prompt.' },
  { name: 'modify_email', desc: 'Rewrite an existing draft from a natural-language instruction.' },
  { name: 'screenshot_email', desc: 'Render any version of a draft to PNG so the agent can see it.' },
  { name: 'send_email', desc: 'Send a draft from a verified domain.' },
  { name: 'list_drafts', desc: "List the user's drafts the agent has touched." },
];

function InstallSection() {
  const snippet = useMemo(() => buildMcpConfigJson(null), []);
  const [copied, setCopied] = useState(false);

  function copySnippet() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Install in your AI tool</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Connect SendFast to Cursor, Claude Desktop, VS Code, or any MCP-aware host. We'll
            mint a fresh API key, hand it to your client, and the five tools below appear in
            your tool picker.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/install">
            Shareable installer <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </div>

      <Card className="mt-6 p-5">
        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          One-click install
        </p>
        <p className="text-foreground mt-1.5 text-sm leading-relaxed">
          Click your host. We'll auto-mint a key named{' '}
          <code className="text-foreground bg-muted rounded px-1 py-0.5 text-xs">
            One-click install — &lt;today&gt;
          </code>{' '}
          and reuse it across hosts in the same browser tab so you don't burn quota.
        </p>
        <div className="mt-4">
          <InstallButtons />
        </div>
        <p className="text-muted-foreground mt-3 text-xs leading-relaxed">
          Cursor and VS Code support deeplink installs. For Claude Desktop or Claude.ai use the{' '}
          <Link to="/install" className="text-primary hover:underline">
            full installer
          </Link>{' '}
          — both need a manual config step.
        </p>
      </Card>

      <Card className="mt-4 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Manual install
          </p>
          <Button variant="ghost" size="sm" onClick={copySnippet}>
            {copied ? (
              <>
                <Check className="size-3" /> Copied
              </>
            ) : (
              <>
                <Copy className="size-3" /> Copy snippet
              </>
            )}
          </Button>
        </div>
        <p className="text-foreground mt-1.5 text-sm leading-relaxed">
          Drop this into your client's MCP config. Replace{' '}
          <code className="text-foreground bg-muted rounded px-1 py-0.5 text-xs">
            sf_live_REPLACE_ME
          </code>{' '}
          with a key from the{' '}
          <Link to="/app/settings?tab=api" className="text-primary hover:underline">
            API keys tab
          </Link>
          .
        </p>
        <pre className="bg-foreground text-background mt-3 overflow-x-auto rounded-md px-3 py-2.5 font-mono text-[11px] leading-snug">
          <code>{snippet}</code>
        </pre>
      </Card>

      <Card className="mt-4 p-5">
        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          What gets installed
        </p>
        <p className="text-foreground mt-1.5 text-sm leading-relaxed">
          Five tools your agent can call. Same surface as the REST API.
        </p>
        <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
          {MCP_TOOLS.map((t) => (
            <li
              key={t.name}
              className="border-border bg-muted/30 flex flex-col gap-1.5 rounded-md border p-3"
            >
              <code className="text-foreground bg-background w-fit rounded px-1.5 py-0.5 font-mono text-xs">
                {t.name}
              </code>
              <span className="text-muted-foreground text-xs leading-relaxed">{t.desc}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ===========================================================================
// Appearance section
// ===========================================================================

const THEME_OPTIONS: Array<{
  value: Theme;
  label: string;
  description: string;
  icon: typeof Sun;
}> = [
  {
    value: 'system',
    label: 'System',
    description: 'Match your device’s light or dark appearance.',
    icon: Monitor,
  },
  {
    value: 'light',
    label: 'Light',
    description: 'Warm cream background — best in daylight.',
    icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dimmed surfaces and softer contrast for evening use.',
    icon: Moon,
  },
];

function AppearanceSection() {
  const { theme, setTheme } = useTheme();

  return (
    <div>
      <h2 className="text-xl font-semibold tracking-tight">Appearance</h2>
      <p className="text-muted-foreground mt-1 max-w-xl text-sm">
        Choose how SendFast looks in the app. Your choice is saved on this device.
      </p>

      <Card className="mt-6 p-5">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Theme
        </h3>
        <RadioGroup
          value={theme}
          onValueChange={(value: string) => setTheme(value as Theme)}
          className="mt-4 gap-3"
        >
          {THEME_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const id = `theme-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={id}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                  theme === opt.value
                    ? 'border-primary/40 bg-accent/50'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <RadioGroupItem value={opt.value} id={id} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Icon className="text-muted-foreground size-4" aria-hidden />
                    {opt.label}
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">{opt.description}</p>
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </Card>
    </div>
  );
}

// ===========================================================================
// Account section
// ===========================================================================

function AccountSection() {
  const [data, setData] = useState<AccountResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setData(await getAccount());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load account');
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Account</h2>
          <p className="text-muted-foreground mt-1 max-w-xl text-sm">
            Your profile and the data we hold for you. Sign out or delete your account here.
          </p>
        </div>
        <Button variant="outline" asChild>
          {/* Worker endpoint clears cookies + 302 back to landing — bypasses
              SPA router so we don't need a /sign-out React route. */}
          <a href="/sign-out">
            <LogOut className="size-4" /> Sign out
          </a>
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive" className="mt-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {data === null && !error ? (
        <AccountLoading />
      ) : data ? (
        <div className="mt-6 space-y-6">
          <ProfileCard data={data} onAvatarChanged={refetch} />
          <YourDataCard counts={data.counts} />
          <DangerZoneCard onDelete={() => setDeleteOpen(true)} />
        </div>
      ) : null}

      {deleteOpen && data ? (
        <DeleteAccountModal
          data={data}
          onClose={() => setDeleteOpen(false)}
        />
      ) : null}
    </div>
  );
}

function AccountLoading() {
  return (
    <div className="mt-6 space-y-6">
      {[0, 1, 2].map((i) => (
        <Card key={i} className="p-5">
          <Skeleton className="h-5 w-1/4" />
          <Skeleton className="mt-3 h-3 w-2/3" />
          <Skeleton className="mt-2 h-3 w-1/2" />
        </Card>
      ))}
    </div>
  );
}

function ProfileCard({
  data,
  onAvatarChanged,
}: {
  data: AccountResponse;
  onAvatarChanged: () => void;
}) {
  const { profile, billing } = data;
  const initial = profile.email[0]?.toUpperCase() ?? '?';
  const joined = new Date(profile.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const planLabel =
    billing.tier === 'free' ? 'Free' : billing.tier === 'pro' ? 'Pro' : 'Scale';

  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        Profile
      </h3>
      <div className="mt-4 flex flex-wrap items-start gap-5">
        <AvatarUploader
          image={profile.image}
          fallback={initial}
          onChanged={onAvatarChanged}
        />
        <dl className="grid min-w-0 flex-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground text-xs">Email</dt>
            <dd className="mt-0.5 flex items-center gap-2 truncate">
              <span className="truncate font-medium">{profile.email}</span>
              {profile.emailVerified ? (
                <Badge
                  variant="outline"
                  className="border-success/30 bg-success/10 text-success rounded-full text-[10px] font-medium"
                >
                  <CheckCircle2 className="size-3" /> Verified
                </Badge>
              ) : null}
            </dd>
          </div>
          {profile.name ? (
            <div>
              <dt className="text-muted-foreground text-xs">Name</dt>
              <dd className="mt-0.5 truncate">{profile.name}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-muted-foreground text-xs">Signed in with</dt>
            <dd className="mt-0.5">{providerLabel(profile.signInProvider)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Joined</dt>
            <dd className="mt-0.5">{joined}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Current plan</dt>
            <dd className="mt-0.5 flex items-center gap-2">
              <span className="font-medium">{planLabel}</span>
              {billing.hasActiveSubscription ? (
                <Badge variant="secondary" className="rounded-full text-[10px] font-medium">
                  Active
                </Badge>
              ) : null}
            </dd>
          </div>
        </dl>
      </div>
    </Card>
  );
}

function providerLabel(p: SignInProvider): string {
  if (p === 'google') return 'Google';
  if (p === 'credential') return 'Email + password';
  return 'Unknown';
}

// ---------------------------------------------------------------------------
// AvatarUploader
// ---------------------------------------------------------------------------
//
// Inline avatar editor for ProfileCard. Hidden <input type="file"> + a click
// on the avatar opens the picker; explicit "Remove" button reverts to the
// initials fallback.
//
// On success we call:
//   - `onChanged()` so AccountSection re-fetches /api/account (drives the
//     <img src> here)
//   - `useSession().refresh()` so /api/me re-fires and the UserMenu in the
//     AppHeader picks up the new image without a full page reload.
//
// Validation is duplicated client-side (size + MIME) purely as UX — server
// is the source of truth and re-validates.

const AVATAR_MAX_BYTES_CLIENT = 2 * 1024 * 1024;
const AVATAR_ACCEPTED_MIME = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

function AvatarUploader({
  image,
  fallback,
  onChanged,
}: {
  image: string | null;
  fallback: string;
  onChanged: () => void;
}) {
  const { refresh: refreshSession } = useSession();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File) {
    setErr(null);
    if (file.size > AVATAR_MAX_BYTES_CLIENT) {
      setErr(`Image too large (max ${AVATAR_MAX_BYTES_CLIENT / 1024 / 1024} MB)`);
      return;
    }
    if (!AVATAR_ACCEPTED_MIME.includes(file.type)) {
      setErr('Use a PNG, JPEG, GIF, or WebP image.');
      return;
    }
    setBusy('upload');
    try {
      await uploadAvatar(file);
      onChanged();
      void refreshSession();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not upload image.');
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove() {
    setErr(null);
    setBusy('remove');
    try {
      await removeAvatar();
      onChanged();
      void refreshSession();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not remove image.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy !== null}
        className="group focus-visible:ring-ring/40 relative rounded-full outline-none focus-visible:ring-2 disabled:cursor-not-allowed"
        aria-label="Change profile picture"
      >
        <Avatar className="size-14">
          {image ? <AvatarImage src={image} alt="" /> : null}
          <AvatarFallback className="text-lg">{fallback}</AvatarFallback>
        </Avatar>
        {/* Hover scrim doubles as a focus indicator — click target is the
            entire avatar so the affordance has to be obvious without text. */}
        <span className="bg-foreground/55 absolute inset-0 flex items-center justify-center rounded-full text-[10px] font-medium uppercase tracking-wider text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          {busy === 'upload' ? 'Uploading…' : 'Change'}
        </span>
      </button>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy !== null}
          >
            {busy === 'upload' ? 'Uploading…' : image ? 'Change photo' : 'Upload photo'}
          </Button>
          {image ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleRemove}
              disabled={busy !== null}
              className="text-muted-foreground hover:text-foreground"
            >
              {busy === 'remove' ? 'Removing…' : 'Remove'}
            </Button>
          ) : null}
        </div>
        <p className="text-muted-foreground text-xs">PNG, JPG, GIF, or WebP. Max 2 MB.</p>
        {err ? <p className="text-destructive text-xs">{err}</p> : null}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={AVATAR_ACCEPTED_MIME.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset value so picking the same file twice still fires onChange.
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
    </div>
  );
}

function YourDataCard({ counts }: { counts: AccountResponse['counts'] }) {
  const items = [
    { label: 'Drafts', value: counts.drafts },
    { label: 'Sent emails', value: counts.sends },
    { label: 'Sending domains', value: counts.domains },
    { label: 'Active API keys', value: counts.apiKeys },
    { label: 'Integrations', value: counts.integrations },
  ];
  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        Your data
      </h3>
      <p className="text-muted-foreground mt-2 text-sm">
        A snapshot of what SendFast stores for you. Deleting your account wipes all of this.
      </p>
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
        {items.map((item) => (
          <div key={item.label}>
            <dt className="text-muted-foreground text-xs">{item.label}</dt>
            <dd className="mt-0.5 text-lg font-semibold tabular-nums">{item.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function DangerZoneCard({ onDelete }: { onDelete: () => void }) {
  return (
    <Card className="border-destructive/30 bg-destructive/[0.03] p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-destructive size-4" />
            <h3 className="text-sm font-medium">Delete account</h3>
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            Permanently delete your account, drafts, sending domains, API keys, and
            integrations. If you have an active paid subscription, it will be cancelled
            immediately. This cannot be undone.
          </p>
        </div>
        <Button variant="destructive" onClick={onDelete}>
          <Trash2 className="size-4" /> Delete account
        </Button>
      </div>
    </Card>
  );
}

function DeleteAccountModal({
  data,
  onClose,
}: {
  data: AccountResponse;
  onClose: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expectedEmail = data.profile.email;
  const matchesEmail = confirmText.trim().toLowerCase() === expectedEmail.toLowerCase();
  const hasActiveSub = data.billing.hasActiveSubscription;

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !matchesEmail) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteAccount(confirmText.trim());
      // Server has already cleared cookies — hard-reload to the landing
      // page so any cached SPA state is purged along with the session.
      window.location.assign('/');
    } catch (e) {
      setSubmitting(false);
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Could not delete account');
      }
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleConfirm}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-destructive size-5" />
              Delete your account?
            </DialogTitle>
            <DialogDescription>
              This is permanent — we cannot recover your data once it's gone.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="bg-muted/50 border-border rounded-md border p-3 text-sm">
              <p className="text-muted-foreground font-medium">We'll delete:</p>
              <ul className="mt-2 space-y-1 text-sm">
                <DeleteListItem label="Drafts" n={data.counts.drafts} />
                <DeleteListItem label="Sent emails (history)" n={data.counts.sends} />
                <DeleteListItem label="Sending domains" n={data.counts.domains} />
                <DeleteListItem label="API keys" n={data.counts.apiKeys} />
                <DeleteListItem label="Connected integrations" n={data.counts.integrations} />
              </ul>
              {hasActiveSub ? (
                <p className="text-destructive mt-3 text-xs">
                  Your {data.billing.tier === 'pro' ? 'Pro' : 'Scale'} subscription will be
                  cancelled immediately (no proration / refund).
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label className="text-sm">
                Type <code className="text-foreground">{expectedEmail}</code> to confirm
              </Label>
              <Input
                type="text"
                autoFocus
                autoComplete="off"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedEmail}
                disabled={submitting}
                className="font-mono text-sm"
              />
            </div>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={!matchesEmail || submitting}>
              {submitting ? 'Deleting…' : 'Permanently delete account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteListItem({ label, n }: { label: string; n: number }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{n}</span>
    </li>
  );
}
